import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Paper, TextField, Typography, Box } from "@mui/material";
// cmps
import Toast from "../Toast";
// hooks
import useSend from "../../hooks/useSend";
// types
import type { GatewayId } from "../../types/gateway";
import type { CmdGatewayActionZigbeeDiagnostics } from "../../types/gupport";
import type { ZigbeeStatus } from "../../types/gupport";

type Props = Readonly<{
	gatewayId: GatewayId;
}>;

const GatewayZigbeeChipStatus = ({ gatewayId }: Props) => {
	const send = useSend();

	const { t } = useTranslation();

	const [zigBeeStatusLogs, setZigBeeStatusLogs] = useState<ReadonlyArray<ZigbeeStatus> | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [showGenericErrorMsg, setShowGenericErrorMsg] = useState<number | undefined>(undefined);

	const handleCheckChipStatus = () => {
		setLoading(true);
		const cmd = {
			action: "gatewayAction",
			channel: "hornbach",
			function: "check_ezsp",
			gatewayId: gatewayId,
			module: "zigbee_diagnostics",
			params: [],
		} as const satisfies CmdGatewayActionZigbeeDiagnostics;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				setShowGenericErrorMsg(undefined);
				setZigBeeStatusLogs(msg.payload.data);
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setLoading(false);
		});
	};

	return (
		<Paper sx={{ p: 1, height: "calc(100vh - 150px)" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
				}}
			>
				<Typography variant="h6">{t("gateway.zigbeeChipStatus.title")}</Typography>
				<Button
					variant="contained"
					loading={loading}
					onClick={handleCheckChipStatus}
				>
					{t("gateway.zigbeeChipStatus.buttonTitle")}
				</Button>
			</Box>
			<TextField
				value={zigBeeStatusLogs ? JSON.stringify(zigBeeStatusLogs, null, 2) : undefined}
				placeholder={zigBeeStatusLogs ? "" : t("gateway.zigbeeChipStatus.placeholder")}
				fullWidth={true}
				multiline={true}
				variant="outlined"
				minRows={5}
				maxRows={10}
				slotProps={{
					input: {
						readOnly: true,
						sx: {
							"& textarea": {
								resize: "vertical",
								overflow: "auto",
							},
						},
					},
				}}
			/>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGenericErrorMsg}
				onClose={setShowGenericErrorMsg}
				message={t("toast.genericErrorMsg")}
			/>
		</Paper>
	);
};

export default GatewayZigbeeChipStatus;
